/**
 * Pool Address Manager
 *
 * Manages pool addresses using a combination of:
 * 1. Static addresses from pool-addresses.json (pre-fetched at build time)
 * 2. In-memory cache for newly discovered pools (created after static file was generated)
 *
 * This optimization reduces RPC calls by not re-fetching addresses we already know.
 */

import { Address } from 'viem';
import { readContracts } from '@wagmi/core';
import { config } from '@/config/index';
import { ChainType } from '@/constants/chain-specific';

// Type definitions for the pool addresses JSON structure
interface DexPoolData {
  name: string;
  factoryAddress: string;
  totalPools: number;
  pools: string[];
}

interface ChainPoolData {
  chainName: string;
  dexes: Record<string, DexPoolData>;
}

interface PoolAddressData {
  version: string;
  lastUpdated: string;
  chains: Record<string, ChainPoolData>;
}

// Import the JSON data with type assertion
// eslint-disable-next-line @typescript-eslint/no-var-requires
let poolAddressData: PoolAddressData;
try {
  // Dynamic import for SSR compatibility
  poolAddressData = require('../data/pool-addresses.json') as PoolAddressData;
} catch {
  // Fallback empty structure if file doesn't exist
  poolAddressData = {
    version: '1.0.0',
    lastUpdated: new Date().toISOString(),
    chains: {},
  };
}

// Factory ABI for fetching new pairs
const FACTORY_ABI = [
  {
    inputs: [],
    name: 'allPairsLength',
    outputs: [{ type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ type: 'uint256' }],
    name: 'allPairs',
    outputs: [{ type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const;

// In-memory cache for newly discovered pools (pools created after static file was generated)
// Structure: chainId -> dexSlug -> Address[]
const newPoolsCache: Map<string, Address[]> = new Map();

// Helper to create cache key
function getCacheKey(chainId: number, dexSlug: string): string {
  return `${chainId}-${dexSlug}`;
}

/**
 * Get static pool addresses from the pre-generated JSON file
 */
export function getStaticPoolAddresses(chainId: number, dexSlug: string): Address[] {
  const chainData = poolAddressData.chains[chainId.toString()];
  if (!chainData) {
    console.log(`No static pool data for chain ${chainId}`);
    return [];
  }

  const dexData = chainData.dexes[dexSlug];
  if (!dexData) {
    console.log(`No static pool data for DEX ${dexSlug} on chain ${chainId}`);
    return [];
  }

  return dexData.pools as Address[];
}

/**
 * Get the count of static pools for a DEX
 */
export function getStaticPoolCount(chainId: number, dexSlug: string): number {
  return getStaticPoolAddresses(chainId, dexSlug).length;
}

/**
 * Get cached new pool addresses (pools discovered since static file was generated)
 */
export function getCachedNewPools(chainId: number, dexSlug: string): Address[] {
  const key = getCacheKey(chainId, dexSlug);
  return newPoolsCache.get(key) || [];
}

/**
 * Add newly discovered pools to the memory cache
 */
export function cacheNewPools(chainId: number, dexSlug: string, pools: Address[]): void {
  const key = getCacheKey(chainId, dexSlug);
  const existing = newPoolsCache.get(key) || [];

  // Only add pools that aren't already cached
  const newPools = pools.filter(pool => !existing.includes(pool));
  if (newPools.length > 0) {
    newPoolsCache.set(key, [...existing, ...newPools]);
    console.log(`Cached ${newPools.length} new pools for ${dexSlug} on chain ${chainId}`);
  }
}

/**
 * Get all known pool addresses (static + cached new)
 */
export function getAllKnownPools(chainId: number, dexSlug: string): Address[] {
  const staticPools = getStaticPoolAddresses(chainId, dexSlug);
  const newPools = getCachedNewPools(chainId, dexSlug);
  return [...staticPools, ...newPools];
}

/**
 * Fetch any new pools from the factory that we don't have in our static file or cache
 *
 * This is the key optimization - we only query the factory for pools at indices
 * beyond what we already know about.
 */
export async function fetchNewPools(
  factoryAddress: Address,
  chainId: ChainType,
  dexSlug: string,
  batchSize: number = 100
): Promise<Address[]> {
  try {
    // Get current total from factory
    const [pairsLengthResult] = await readContracts(config, {
      contracts: [
        {
          address: factoryAddress,
          abi: FACTORY_ABI,
          functionName: 'allPairsLength',
          chainId,
        },
      ],
    });

    if (pairsLengthResult.status !== 'success') {
      console.error('Failed to get pairs length from factory');
      return [];
    }

    const totalPairs = Number(pairsLengthResult.result);
    const knownPools = getAllKnownPools(chainId, dexSlug);
    const knownCount = knownPools.length;

    console.log(`[PoolManager] ${dexSlug}: Factory has ${totalPairs} pools, we know ${knownCount}`);

    // If we already know all pools, nothing to fetch
    if (knownCount >= totalPairs) {
      return [];
    }

    const newPoolCount = totalPairs - knownCount;
    console.log(`[PoolManager] ${dexSlug}: Fetching ${newPoolCount} new pool addresses...`);

    const newPools: Address[] = [];

    // Fetch new pools in batches
    for (let i = knownCount; i < totalPairs; i += batchSize) {
      const batchEnd = Math.min(i + batchSize, totalPairs);
      const batchLength = batchEnd - i;

      const contracts = Array.from({ length: batchLength }, (_, idx) => ({
        address: factoryAddress,
        abi: FACTORY_ABI,
        functionName: 'allPairs' as const,
        args: [BigInt(i + idx)],
        chainId,
      }));

      const results = await readContracts(config, { contracts });

      for (const result of results) {
        if (result.status === 'success' && result.result) {
          newPools.push(result.result as Address);
        }
      }
    }

    // Cache the newly discovered pools
    if (newPools.length > 0) {
      cacheNewPools(chainId, dexSlug, newPools);
    }

    return newPools;
  } catch (error) {
    console.error(`[PoolManager] Error fetching new pools for ${dexSlug}:`, error);
    return [];
  }
}

/**
 * Get all pool addresses for a DEX, fetching any new ones first
 *
 * This is the main function to use when you need all pool addresses.
 * It returns static + cached + newly fetched pools.
 */
export async function getPoolAddresses(
  factoryAddress: Address,
  chainId: ChainType,
  dexSlug: string
): Promise<Address[]> {
  // First, try to fetch any new pools we don't know about
  await fetchNewPools(factoryAddress, chainId, dexSlug);

  // Return all known pools
  return getAllKnownPools(chainId, dexSlug);
}

/**
 * Clear the in-memory cache (useful for testing or forced refresh)
 */
export function clearPoolCache(): void {
  newPoolsCache.clear();
  console.log('[PoolManager] Cleared pool cache');
}

/**
 * Get pool address manager statistics
 */
export function getPoolStats(): {
  staticPools: Record<string, number>;
  cachedNewPools: Record<string, number>;
} {
  const stats = {
    staticPools: {} as Record<string, number>,
    cachedNewPools: {} as Record<string, number>,
  };

  // Count static pools
  for (const [chainId, chainData] of Object.entries(poolAddressData.chains)) {
    for (const [dexSlug, dexData] of Object.entries(chainData.dexes)) {
      const key = `${chainId}-${dexSlug}`;
      stats.staticPools[key] = dexData.pools.length;
    }
  }

  // Count cached new pools
  for (const [key, pools] of newPoolsCache.entries()) {
    stats.cachedNewPools[key] = pools.length;
  }

  return stats;
}
